import { CheckCircle2, SquareArrowOutUpRight } from 'lucide-react'
import type { FeeItem, RoutePath } from '@/lib/api-types'
import { FeeReceipt } from './fee-receipt'
import { RouteLine } from './route-line'
import { Steps } from './steps'

export type FlowLink = { label: string; href: string }

/**
 * The progress card every flow shows (deposit, withdraw, buy, sell): the
 * numbered steps with the loader bar, one line saying what is happening
 * to the money, links to the transactions and, once done, the fee receipt.
 * Progress texts only, no explanations (John, Oct 29 2026).
 */
export function FlowProgress({
  labels,
  step,
  done = false,
  failed = false,
  idle = false,
  status,
  links = [],
  fees,
  route,
  action,
  flowKey,
}: {
  labels: readonly string[]
  step: number
  /** the record's id: the bar remembers where it got to across a close and reopen */
  flowKey?: string
  done?: boolean
  failed?: boolean
  /** nothing has started yet: no current step, the bar waits */
  idle?: boolean
  status: React.ReactNode
  links?: FlowLink[]
  /** what the finished flow paid, line by line */
  fees?: FeeItem[] | null
  /** the pools the finished swap went through */
  route?: RoutePath[] | null
  /** a retry or "new" button under a finished or failed flow */
  action?: React.ReactNode
}) {
  return (
    <div className="card flex flex-col gap-4 p-4">
      <Steps labels={labels} step={failed ? -1 : step} done={done} idle={idle} flowKey={flowKey} />
      <div className="flex flex-col items-center gap-2 py-1 text-center text-sm empty:hidden">
        {status != null && status !== false ? (
          <div className={`flex flex-wrap items-center justify-center gap-1.5 ${failed ? 'text-negative' : done ? 'font-medium' : 'text-muted'}`}>
            {done && !failed ? <CheckCircle2 className="size-5 shrink-0 text-accent" /> : null}
            {status}
          </div>
        ) : null}
        {links.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2">
            {links.map((link) => (
              <a key={link.href} className="btn btn-ghost !px-2 !py-1 text-xs" href={link.href} target="_blank" rel="noreferrer">
                {link.label} <SquareArrowOutUpRight className="size-3.5" />
              </a>
            ))}
          </div>
        ) : null}
        {action}
      </div>
      {done ? <RouteLine route={route} className="border-t border-line pt-3" /> : null}
      {done ? <FeeReceipt items={fees} className="border-t border-line pt-3" /> : null}
    </div>
  )
}
